import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Upload, Play, Eye, EyeOff } from 'lucide-react';
import api from '../../utils/api';

function AdminVideosPage() {
  const [reels, setReels] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    video_url: '',
    thumbnail_url: '',
    product_id: '',
    order: 0,
    active: true,
  });
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reelsData, productsData] = await Promise.all([
        api.getReels(),
        api.getProducts(),
      ]);
      setReels(reelsData.reels || []);
      setProducts(productsData.products || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (reel) => {
    setFormData({
      id: reel.id,
      title: reel.title,
      video_url: reel.video_url,
      thumbnail_url: reel.thumbnail_url,
      product_id: reel.product_id || '',
      order: reel.order || 0,
      active: reel.active !== false,
    });
    setShowDialog(true);
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      video_url: '',
      thumbnail_url: '',
      product_id: '',
      order: reels.length,
      active: true,
    });
    setShowDialog(true);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 200 * 1024 * 1024) { // 200MB limit for Stream
      toast.error('Video size must be less than 200MB');
      return;
    }

    setUploadingVideo(true);
    try {
      toast.info('📤 Getting upload URL from Cloudflare...');
      
      // Get Cloudflare Stream upload URL
      const uploadData = await api.adminGetStreamUploadUrl(token);
      
      if (!uploadData.upload_url) {
        throw new Error('Failed to get upload URL');
      }
      
      toast.info('⬆️ Uploading video to Cloudflare Stream... This may take a minute.');
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      // Upload to Cloudflare Stream with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minute timeout
      
      const uploadResponse = await fetch(uploadData.upload_url, {
        method: 'POST',
        body: formDataUpload,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      // Cloudflare Stream direct upload may return empty body on success
      // The video_id was already provided in the upload URL response
      if (uploadResponse.ok || uploadResponse.status === 200) {
        let videoUid = uploadData.video_id;
        
        // Try to parse JSON response if available
        try {
          const responseText = await uploadResponse.text();
          if (responseText && responseText.trim()) {
            const result = JSON.parse(responseText);
            console.log('Cloudflare Stream response:', result);
            videoUid = result.result?.uid || result.uid || uploadData.video_id;
          }
        } catch (parseError) {
          // Response may be empty for direct uploads - that's OK
          console.log('No JSON response from Cloudflare (expected for direct uploads)');
        }
        
        // Use iframe embed URL for video playback
        const iframeUrl = `https://iframe.cloudflarestream.com/${videoUid}`;
        
        // Auto-generate thumbnail from Cloudflare Stream (at 1 second)
        const autoThumbnail = `https://videodelivery.net/${videoUid}/thumbnails/thumbnail.jpg?time=1s&height=480`;
        
        setFormData(prev => ({ 
          ...prev, 
          video_url: iframeUrl,
          video_id: videoUid,
          thumbnail_url: autoThumbnail, // Auto-set thumbnail
        }));
        
        toast.success('✅ Video uploaded! Thumbnail auto-generated from video.');
      } else {
        const errorText = await uploadResponse.text();
        console.error('Stream upload error:', uploadResponse.status, errorText);
        toast.error('Video upload failed: ' + (errorText || `HTTP ${uploadResponse.status}`));
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.name === 'AbortError') {
        toast.error('Upload timed out. Please try a smaller video or check your connection.');
      } else {
        toast.error('Failed to upload video: ' + error.message);
      }
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingThumbnail(true);
    try {
      const uploadData = await api.adminGetCloudflareUploadUrl(token);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await fetch(uploadData.upload_url, {
        method: 'POST',
        body: formData,
      });

      if (uploadResponse.ok) {
        const result = await uploadResponse.json();
        const thumbnailUrl = `https://imagedelivery.net/${result.result.id}/public`;
        setFormData(prev => ({ ...prev, thumbnail_url: thumbnailUrl }));
        toast.success('Thumbnail uploaded successfully!');
      } else {
        toast.error('Thumbnail upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.video_url || !formData.thumbnail_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const reelData = {
        title: formData.title,
        video_url: formData.video_url,
        thumbnail_url: formData.thumbnail_url,
        product_id: formData.product_id || null,
        order: parseInt(formData.order),
        active: formData.active,
      };

      if (formData.id) {
        // Update existing reel - need to implement backend endpoint
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reels/${formData.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(reelData),
        });

        if (response.ok) {
          toast.success('Video updated successfully!');
        } else {
          toast.error('Failed to update video');
        }
      } else {
        // Create new reel
        await api.adminCreateReel(reelData, token);
        toast.success('Video created successfully!');
      }

      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reelId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await api.adminDeleteReel(reelId, token);
      toast.success('Video deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleToggleActive = async (reel) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reels/${reel.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ ...reel, active: !reel.active }),
      });

      if (response.ok) {
        toast.success(reel.active ? 'Video hidden' : 'Video activated');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Video Reels</h1>
            <p className="text-muted-foreground">Manage homepage showcase videos</p>
          </div>
          <Button onClick={handleAdd}>
            <Plus className="w-4 h-4 mr-2" />
            Add Video
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </div>
        ) : reels.length === 0 ? (
          <Card className="p-12 text-center">
            <Play className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Videos Yet</h3>
            <p className="text-muted-foreground mb-4">Add your first video reel to showcase on homepage</p>
            <Button onClick={handleAdd}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Video
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reels.map((reel) => (
              <Card key={reel.id} className="overflow-hidden">
                <AspectRatio ratio={9/16} className="bg-muted">
                  <img
                    src={reel.thumbnail_url}
                    alt={reel.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/10 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </AspectRatio>
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold line-clamp-2 flex-1">{reel.title}</h3>
                    <Badge className={reel.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {reel.active ? 'Active' : 'Hidden'}
                    </Badge>
                  </div>
                  
                  {reel.product_id && (
                    <p className="text-xs text-muted-foreground">
                      Linked to product
                    </p>
                  )}
                  
                  <div className="text-xs text-muted-foreground">
                    Order: {reel.order}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleEdit(reel)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleActive(reel)}
                    >
                      {reel.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(reel.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Edit/Add Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{formData.id ? 'Edit Video' : 'Add New Video'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Video Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Rainbow Unicorn Lunchbox - Perfect for School!"
                />
              </div>

              <div>
                <Label htmlFor="video_upload">Upload Video (MP4, WebM, MOV) *</Label>
                <div className="space-y-2">
                  <Input
                    id="video_upload"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    disabled={uploadingVideo}
                  />
                  {uploadingVideo && (
                    <p className="text-xs text-muted-foreground flex items-center">
                      <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                      Uploading to Cloudflare Stream... This may take a minute.
                    </p>
                  )}
                  {formData.video_url && (
                    <div className="space-y-2">
                      <p className="text-xs text-green-600">✓ Video uploaded to Cloudflare Stream</p>
                      {/* Video Preview */}
                      {formData.video_url.includes('cloudflarestream') && (
                        <div className="w-48 aspect-video bg-black rounded overflow-hidden">
                          <iframe
                            src={formData.video_url}
                            className="w-full h-full"
                            allow="autoplay; picture-in-picture"
                            title="Video preview"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Videos are hosted on Cloudflare Stream with global CDN. Max size: 200MB
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Or paste a video URL from <a href="https://www.pexels.com/videos/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Pexels</a>:
                  </p>
                  <Input
                    id="video_url_manual"
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="Or paste URL: https://videos.pexels.com/..."
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="thumbnail_upload">Thumbnail (Auto-generated or Upload)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Thumbnail is auto-generated from video. Upload custom one to override.
                </p>
                <Input
                  id="thumbnail_upload"
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  disabled={uploadingThumbnail}
                />
                {uploadingThumbnail && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                    Uploading thumbnail...
                  </p>
                )}
                {formData.thumbnail_url && (
                  <div className="mt-2">
                    <p className="text-xs text-green-600 mb-1">✓ Thumbnail ready</p>
                    <img 
                      src={formData.thumbnail_url} 
                      alt="Thumbnail preview" 
                      className="w-32 h-48 object-cover rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="product_id">Link to Product (Optional)</Label>
                <select
                  id="product_id"
                  className="w-full h-10 rounded-md border border-input bg-background px-3"
                  value={formData.product_id}
                  onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                >
                  <option value="">No product link</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Users can click to view this product from the video
                </p>
              </div>

              <div>
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Lower numbers appear first (0 = first position)
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="active" className="cursor-pointer">
                  Active (show on homepage)
                </Label>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving || uploadingVideo || uploadingThumbnail} className="flex-1">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    formData.id ? 'Update Video' : 'Create Video'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminVideosPage;
